import { useState, useCallback } from "react";

const DragDropHandler = ({
  placedFields,
  setPlacedFields,
  scale,
  onFieldSettingsClick,
  onFieldDelete,
  onFieldDuplicate,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [draggedField, setDraggedField] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const handleMouseDown = (e, field) => {
    // Prevent dragging if clicking on input, textarea, or select elements
    // But allow dragging on signature fields even if they contain images or buttons
    const isSignatureField = field.type === "signature" || field.type === "Signature";
    const isInteractiveElement =
      e.target.tagName === "INPUT" ||
      e.target.tagName === "TEXTAREA" ||
      e.target.tagName === "SELECT" ||
      e.target.tagName === "BUTTON";

    if (isInteractiveElement && !isSignatureField) {
      return;
    }

    // Also prevent dragging if clicking on control buttons
    if (e.target.closest(".control-btn")) {
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const edgeThreshold = 10;
    const nearRightEdge = e.clientX > rect.right - edgeThreshold;
    const nearBottomEdge = e.clientY > rect.bottom - edgeThreshold;

    if (nearRightEdge || nearBottomEdge) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    // Calculate offset accounting for zoom level
    const offsetX = (e.clientX - rect.left) / scale;
    const offsetY = (e.clientY - rect.top) / scale;

    setIsDragging(true);
    setDraggedField(field);
    setDragOffset({ x: offsetX, y: offsetY });
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging || !draggedField) {
        return;
      }

      const pdfContainer = document.querySelector(
        ".pdf-page-wrapper .react-pdf__Page",
      );
      if (!pdfContainer) return;

      const rect = pdfContainer.getBoundingClientRect();

      const draggedEl = document.querySelector(
        `.placed-field[data-field-id="${draggedField.id}"]`,
      );
      const draggedRect = draggedEl?.getBoundingClientRect();
      const fieldWidth = (draggedRect?.width || 0) / scale;
      const fieldHeight = (draggedRect?.height || 0) / scale;

      // Calculate position accounting for zoom level
      let newX = (e.clientX - rect.left) / scale - dragOffset.x;
      let newY = (e.clientY - rect.top) / scale - dragOffset.y;

      // Calculate bounds in normalized coordinates
      const containerWidth = rect.width / scale;
      const containerHeight = rect.height / scale;
      const maxX = Math.max(0, containerWidth - fieldWidth);
      const maxY = Math.max(0, containerHeight - fieldHeight);

      // Constrain position within bounds
      newX = Math.min(Math.max(0, newX), maxX);
      newY = Math.min(Math.max(0, newY), maxY);

      setPlacedFields((prevFields) =>
        prevFields.map((field) =>
          field.id === draggedField.id ? { ...field, x: newX, y: newY } : field,
        ),
      );
    },
    [isDragging, draggedField, dragOffset, scale, setPlacedFields],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDraggedField(null);
    setDragOffset({ x: 0, y: 0 });
  }, []);

  const handleSettingsClick = (field) => {
    onFieldSettingsClick(field);
  };

  const handleDeleteField = (fieldId) => {
    onFieldDelete(fieldId);
  };

  const handleDuplicateField = (field) => {
    onFieldDuplicate(field);
  };

  return {
    isDragging,
    draggedField,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleSettingsClick,
    handleDeleteField,
    handleDuplicateField,
  };
};

export default DragDropHandler;
